// Superadmin shell -- reached only via LoginForm's hidden icon +
// AppContext's loginSuperadmin (real Supabase Auth + a server-side
// is_platform_superadmin() check, see both for why). Deliberately its own
// top-level view, not a mode grafted onto AdminDashboard -- a superadmin
// isn't a member of any one tenant, so none of AdminDashboard's
// tenant-scoped assumptions apply here.
//
// Phase 1: tenant list + per-tenant module on/off toggles (tenant_modules,
// gated by app_modules as the catalog of what a module even is). Phase 2
// (SuperadminPricing.tsx): platform-wide module rates + multi-module
// discount. Phase 3 (SuperadminAnalytics.tsx): per-tenant fleet/driver/
// module snapshot. Phase 4 (SuperadminBilling.tsx): standard Stripe config.
//
// Styled to match the rest of DMS (Card/shadow language, module icons
// mirroring AdminDashboard's own tab icons, the same AnimatedTabBar "gold
// bubble" nav as DriverDashboard) rather than the plain bordered divs this
// screen launched with -- a superadmin still looks at this daily, it
// deserves the same polish as every tenant-facing screen.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { LogOut, ShieldCheck, Building2, Boxes, BarChart3, Wallet, CheckCircle2, Plus, UserPlus, Users, History, Megaphone, Download, Palette } from 'lucide-react';
import { moduleIcon } from '@/lib/moduleIcons';
import { MODULE_TABS } from '@/lib/moduleTabs';
import { logPlatformAdminAction } from '@/lib/platformAuditLog';
import { CORE_MODULE_KEYS } from '@/lib/coreModules';
import { type PlatformBillingBasis } from '@/lib/billableUnits';
import AnimatedTabBar, { type AnimatedTabItem } from '@/components/ui/animated-tab-bar';
import SuperadminPricing from './SuperadminPricing';
import SuperadminAnalytics from './SuperadminAnalytics';
import SuperadminBilling from './SuperadminBilling';
import SuperadminUsers from './SuperadminUsers';
import SuperadminAuditLog from './SuperadminAuditLog';
import SuperadminAnnouncement from './SuperadminAnnouncement';
import SuperadminBranding from './SuperadminBranding';
import SuperadminTenantBilling from './SuperadminTenantBilling';

interface SuperadminDashboardProps {
  onLogout: () => void;
}

interface TenantRow {
  id: string;
  tenant_name: string;
  trading_name: string | null;
  is_active: boolean;
  billing_contact_name: string | null;
  billing_contact_email: string | null;
  platform_billing_basis: PlatformBillingBasis;
  stripe_customer_id: string | null;
  payment_restricted: boolean;
}

interface ModuleCatalogRow {
  module_key: string;
  module_name: string;
  description: string | null;
  sort_order: number;
}

type SuperadminTab = 'tenants' | 'pricing' | 'analytics' | 'billing' | 'superadmins' | 'announcement' | 'branding' | 'auditlog';

// Dashboard & Settings are base-app, not paid add-ons -- always on for
// every tenant, pinned first, and not toggleable from this screen (per
// Janelle). Everything else is a real paid module. CORE_MODULE_KEYS is
// shared with SuperadminPricing (also excludes them) and coreModules.ts
// has the full rationale.
const CORE_MODULE_ORDER = CORE_MODULE_KEYS;

const NAV_TABS: AnimatedTabItem[] = [
  { value: 'tenants', label: 'Tenants & Modules', icon: Building2 },
  { value: 'pricing', label: 'Pricing', icon: Boxes },
  { value: 'analytics', label: 'Analytics', icon: BarChart3 },
  { value: 'billing', label: 'Billing', icon: Wallet },
  { value: 'superadmins', label: 'Superadmins', icon: Users },
  { value: 'announcement', label: 'Announcement', icon: Megaphone },
  { value: 'branding', label: 'Branding', icon: Palette },
  { value: 'auditlog', label: 'Audit Log', icon: History },
];

const SuperadminDashboard: React.FC<SuperadminDashboardProps> = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState<SuperadminTab>('tenants');
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [modules, setModules] = useState<ModuleCatalogRow[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
  const [enabledByModuleKey, setEnabledByModuleKey] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [billingContactName, setBillingContactName] = useState('');
  const [billingContactEmail, setBillingContactEmail] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [savingActive, setSavingActive] = useState(false);
  const [savingBillingBasis, setSavingBillingBasis] = useState(false);
  const [exportingData, setExportingData] = useState(false);

  const [showAddTenant, setShowAddTenant] = useState(false);
  const [newTenantName, setNewTenantName] = useState('');
  const [newTenantTradingName, setNewTenantTradingName] = useState('');
  const [newTenantLoginCode, setNewTenantLoginCode] = useState('');
  const [creatingTenant, setCreatingTenant] = useState(false);

  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminName, setNewAdminName] = useState('');
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [creatingAdmin, setCreatingAdmin] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [{ data: tenantRows, error: tenantErr }, { data: moduleRows, error: moduleErr }] = await Promise.all([
        // is_active not filtered here -- a superadmin needs to see (and
        // reactivate) paused tenants too, unlike the public
        // tenants_select_active policy every login screen relies on.
        supabase
          .from('tenants')
          .select('id, tenant_name, trading_name, is_active, billing_contact_name, billing_contact_email, platform_billing_basis, stripe_customer_id, payment_restricted')
          .order('tenant_name'),
        supabase.from('app_modules').select('module_key, module_name, description, sort_order').eq('is_active', true).order('sort_order'),
      ]);
      if (tenantErr) toast({ title: 'Error', description: 'Failed to load tenants: ' + tenantErr.message, variant: 'destructive' });
      if (moduleErr) toast({ title: 'Error', description: 'Failed to load modules: ' + moduleErr.message, variant: 'destructive' });
      setTenants((tenantRows as TenantRow[]) || []);
      setModules((moduleRows as ModuleCatalogRow[]) || []);
      setSelectedTenantId((current) => current ?? tenantRows?.[0]?.id ?? null);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    const t = tenants.find((row) => row.id === selectedTenantId);
    setBillingContactName(t?.billing_contact_name ?? '');
    setBillingContactEmail(t?.billing_contact_email ?? '');
  }, [selectedTenantId, tenants]);

  useEffect(() => {
    if (!selectedTenantId) return;
    setModulesLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('tenant_modules')
        .select('module_key, is_enabled')
        .eq('tenant_id', selectedTenantId);
      if (error) {
        toast({ title: 'Error', description: "Failed to load this tenant's modules: " + error.message, variant: 'destructive' });
        setModulesLoading(false);
        return;
      }
      // A module with no row at all for this tenant (e.g. a brand-new
      // tenant that's never had it touched) reads as OFF -- nothing is
      // enabled until a superadmin actually turns it on, which is the
      // right default for onboarding a paying tenant.
      const map: Record<string, boolean> = {};
      (data || []).forEach((row: { module_key: string; is_enabled: boolean }) => {
        map[row.module_key] = row.is_enabled;
      });
      setEnabledByModuleKey(map);
      setModulesLoading(false);
    })();
  }, [selectedTenantId]);

  const toggleModule = async (moduleKey: string, nextEnabled: boolean) => {
    if (!selectedTenantId) return;
    setSavingKey(moduleKey);
    const previous = enabledByModuleKey[moduleKey] ?? false;
    setEnabledByModuleKey((prev) => ({ ...prev, [moduleKey]: nextEnabled }));

    const { error } = await supabase
      .from('tenant_modules')
      .upsert(
        { tenant_id: selectedTenantId, module_key: moduleKey, is_enabled: nextEnabled },
        { onConflict: 'tenant_id,module_key' }
      );

    setSavingKey(null);
    if (error) {
      setEnabledByModuleKey((prev) => ({ ...prev, [moduleKey]: previous }));
      toast({ title: 'Error', description: 'Failed to update module: ' + error.message, variant: 'destructive' });
      return;
    }
    const tenantName = tenants.find((t) => t.id === selectedTenantId)?.tenant_name;
    logPlatformAdminAction('module_toggled', 'tenant_module', `${selectedTenantId}:${moduleKey}`, {
      tenant_name: tenantName,
      module_key: moduleKey,
      is_enabled: nextEnabled,
    });
  };

  const saveBillingContact = async () => {
    if (!selectedTenantId) return;
    setSavingContact(true);
    const { error } = await supabase
      .from('tenants')
      .update({
        billing_contact_name: billingContactName.trim() || null,
        billing_contact_email: billingContactEmail.trim() || null,
      })
      .eq('id', selectedTenantId);
    setSavingContact(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to save billing contact: ' + error.message, variant: 'destructive' });
      return;
    }
    setTenants((prev) =>
      prev.map((t) =>
        t.id === selectedTenantId
          ? { ...t, billing_contact_name: billingContactName.trim() || null, billing_contact_email: billingContactEmail.trim() || null }
          : t
      )
    );
    toast({ title: 'Saved', description: 'Billing contact updated.' });
  };

  const savePlatformBillingBasis = async (basis: PlatformBillingBasis) => {
    if (!selectedTenantId) return;
    setSavingBillingBasis(true);
    const { error } = await supabase.from('tenants').update({ platform_billing_basis: basis }).eq('id', selectedTenantId);
    setSavingBillingBasis(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update billing basis: ' + error.message, variant: 'destructive' });
      return;
    }
    setTenants((prev) => prev.map((t) => (t.id === selectedTenantId ? { ...t, platform_billing_basis: basis } : t)));
    const tenantName = tenants.find((t) => t.id === selectedTenantId)?.tenant_name;
    logPlatformAdminAction('platform_billing_basis_changed', 'tenant', selectedTenantId, { tenant_name: tenantName, basis });
  };

  const toggleTenantActive = async (nextActive: boolean) => {
    if (!selectedTenantId) return;
    setSavingActive(true);
    const { error } = await supabase.from('tenants').update({ is_active: nextActive }).eq('id', selectedTenantId);
    setSavingActive(false);
    if (error) {
      toast({ title: 'Error', description: 'Failed to update tenant status: ' + error.message, variant: 'destructive' });
      return;
    }
    setTenants((prev) => prev.map((t) => (t.id === selectedTenantId ? { ...t, is_active: nextActive } : t)));
    const tenantName = tenants.find((t) => t.id === selectedTenantId)?.tenant_name;
    logPlatformAdminAction('tenant_active_changed', 'tenant', selectedTenantId, {
      tenant_name: tenantName,
      is_active: nextActive,
    });
  };

  const createTenant = async () => {
    if (!newTenantName.trim()) return;
    setCreatingTenant(true);
    const { data, error } = await supabase
      .from('tenants')
      .insert({
        tenant_name: newTenantName.trim(),
        trading_name: newTenantTradingName.trim() || null,
        company_login_code: newTenantLoginCode.trim() || null,
      })
      .select('id, tenant_name, trading_name, is_active, billing_contact_name, billing_contact_email, platform_billing_basis, stripe_customer_id, payment_restricted')
      .single();
    setCreatingTenant(false);
    if (error || !data) {
      toast({ title: 'Error', description: 'Failed to create tenant: ' + error?.message, variant: 'destructive' });
      return;
    }
    setTenants((prev) => [...prev, data as TenantRow].sort((a, b) => a.tenant_name.localeCompare(b.tenant_name)));
    setSelectedTenantId(data.id);
    setShowAddTenant(false);
    setNewTenantName('');
    setNewTenantTradingName('');
    setNewTenantLoginCode('');
    toast({ title: 'Tenant created', description: `${data.trading_name || data.tenant_name} is ready -- add its first admin next.` });
    logPlatformAdminAction('tenant_created', 'tenant', data.id, { tenant_name: data.tenant_name });
  };

  const createAdmin = async () => {
    if (!selectedTenantId || !newAdminName.trim() || newAdminPassword.length < 8) return;
    setCreatingAdmin(true);
    const { data, error } = await supabase.functions.invoke('create-tenant-admin', {
      body: {
        tenant_id: selectedTenantId,
        driver_name: newAdminName.trim(),
        email: newAdminEmail.trim(),
        password: newAdminPassword,
      },
    });
    setCreatingAdmin(false);
    if (error || data?.error) {
      toast({ title: 'Error', description: data?.error || error?.message || 'Failed to create admin login', variant: 'destructive' });
      return;
    }
    setShowAddAdmin(false);
    setNewAdminName('');
    setNewAdminEmail('');
    setNewAdminPassword('');
    toast({ title: 'Admin login created', description: `Share the name and password with ${newAdminName.trim()} directly.` });
    const tenantName = tenants.find((t) => t.id === selectedTenantId)?.tenant_name;
    logPlatformAdminAction('admin_created', 'tenant_admin', selectedTenantId, {
      tenant_name: tenantName,
      driver_name: newAdminName.trim(),
    });
  };

  // Core tables only -- not every one of the ~140 tenant-scoped tables in
  // the schema, but enough to actually hand a leaving tenant something
  // useful (their roster, fleet, module config and CRM accounts). Actual
  // tenant deletion isn't offered here at all -- the blast radius across
  // that many tables is too large for a one-click UI action; that stays a
  // deliberate, manual DB operation.
  const exportTenantData = async () => {
    const tenant = tenants.find((t) => t.id === selectedTenantId);
    if (!tenant) return;
    setExportingData(true);
    const tenantId = tenant.id;
    const [drivers, vehicles, tenantModules, crmAccounts] = await Promise.all([
      supabase.from('drivers').select('*').eq('tenant_id', tenantId),
      supabase.from('vehicles').select('*').eq('tenant_id', tenantId),
      supabase.from('tenant_modules').select('*').eq('tenant_id', tenantId),
      supabase.from('crm_accounts').select('*').eq('tenant_id', tenantId),
    ]);
    setExportingData(false);
    const firstError = drivers.error || vehicles.error || tenantModules.error || crmAccounts.error;
    if (firstError) {
      toast({ title: 'Error', description: 'Failed to export tenant data: ' + firstError.message, variant: 'destructive' });
      return;
    }

    const payload = {
      exported_at: new Date().toISOString(),
      tenant,
      drivers: drivers.data,
      vehicles: vehicles.data,
      tenant_modules: tenantModules.data,
      crm_accounts: crmAccounts.data,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(tenant.trading_name || tenant.tenant_name).replace(/[^a-z0-9]+/gi, '-')}-export-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);

    toast({ title: 'Export ready', description: 'Drivers, vehicles, modules and CRM accounts downloaded as JSON.' });
    logPlatformAdminAction('tenant_data_exported', 'tenant', tenantId, { tenant_name: tenant.tenant_name });
  };

  const selectedTenant = tenants.find((t) => t.id === selectedTenantId) ?? null;
  const coreModules = CORE_MODULE_ORDER.map((key) => modules.find((m) => m.module_key === key)).filter(
    (m): m is ModuleCatalogRow => !!m
  );
  const paidModules = modules.filter((m) => !CORE_MODULE_ORDER.includes(m.module_key));
  const orderedModules = [...coreModules, ...paidModules];
  const enabledCount = paidModules.filter((m) => enabledByModuleKey[m.module_key]).length;

  return (
    <div className="min-h-screen p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 shadow-[0_8px_20px_rgba(255,165,0,0.25)]">
            <ShieldCheck className="h-5 w-5 text-black" />
          </div>
          <div>
            <h1 className="text-2xl font-bold golden-text leading-tight">Superadmin</h1>
            <p className="text-xs text-muted-foreground">Platform administration -- not scoped to any single tenant</p>
          </div>
        </div>
        <Button variant="outline" onClick={onLogout}>
          <LogOut className="h-4 w-4 mr-1.5" />
          Log out
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg shadow-lg p-2 sm:p-4">
        <AnimatedTabBar tabs={NAV_TABS} value={activeTab} onValueChange={(v) => setActiveTab(v as SuperadminTab)} />
      </div>

      {activeTab === 'pricing' ? (
        <SuperadminPricing />
      ) : activeTab === 'analytics' ? (
        <SuperadminAnalytics />
      ) : activeTab === 'billing' ? (
        <SuperadminBilling />
      ) : activeTab === 'superadmins' ? (
        <SuperadminUsers />
      ) : activeTab === 'announcement' ? (
        <SuperadminAnnouncement />
      ) : activeTab === 'branding' ? (
        <SuperadminBranding />
      ) : activeTab === 'auditlog' ? (
        <SuperadminAuditLog />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-6">
          <Card className="shadow-lg bg-card border border-border h-fit">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-xs font-medium text-yellow-500 uppercase tracking-wide flex items-center gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Tenants
                </CardTitle>
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => setShowAddTenant(true)}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              {tenants.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTenantId(t.id)}
                  className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    selectedTenantId === t.id ? 'golden-button' : 'text-gray-300 hover:bg-yellow-900/20'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{t.trading_name || t.tenant_name}</span>
                    {!t.is_active && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300 shrink-0">Inactive</span>
                    )}
                  </div>
                </button>
              ))}
              {tenants.length === 0 && <p className="text-xs text-muted-foreground px-2 py-1">No tenants found.</p>}
            </CardContent>
          </Card>

          <Card className="shadow-lg bg-card border border-border">
            {selectedTenant ? (
              <>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <CardTitle className="text-lg text-foreground">
                        {selectedTenant.trading_name || selectedTenant.tenant_name}
                      </CardTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Modules enabled here control which tabs and settings this tenant's admins can see.
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {!modulesLoading && (
                        <span className="text-xs font-medium text-yellow-500 bg-yellow-900/20 border border-yellow-900/30 rounded-full px-3 py-1">
                          {enabledCount} of {paidModules.length} paid modules enabled
                        </span>
                      )}
                      <div className="flex items-center gap-1.5">
                        <span className={`text-xs font-medium ${selectedTenant.is_active ? 'text-green-500' : 'text-gray-400'}`}>
                          {selectedTenant.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Switch
                          checked={selectedTenant.is_active}
                          onCheckedChange={(checked) => toggleTenantActive(checked)}
                          disabled={savingActive}
                        />
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setShowAddAdmin(true)}>
                        <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                        Add Admin
                      </Button>
                      <Button size="sm" variant="outline" onClick={exportTenantData} disabled={exportingData}>
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        {exportingData ? 'Exporting...' : 'Export Data'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="rounded-lg border border-border bg-secondary/20 p-3.5">
                    <p className="text-xs font-medium text-yellow-500 uppercase tracking-wide mb-2.5">Billing Contact</p>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2.5 items-end">
                      <div>
                        <Label className="text-[11px] text-muted-foreground mb-1 block">Name</Label>
                        <Input
                          className="dark-input h-9"
                          value={billingContactName}
                          onChange={(e) => setBillingContactName(e.target.value)}
                          placeholder="Who to email about billing"
                        />
                      </div>
                      <div>
                        <Label className="text-[11px] text-muted-foreground mb-1 block">Email</Label>
                        <Input
                          className="dark-input h-9"
                          type="email"
                          value={billingContactEmail}
                          onChange={(e) => setBillingContactEmail(e.target.value)}
                          placeholder="billing@example.com"
                        />
                      </div>
                      <Button size="sm" className="golden-button h-9" onClick={saveBillingContact} disabled={savingContact}>
                        {savingContact ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border bg-secondary/20 p-3.5">
                    <p className="text-xs font-medium text-yellow-500 uppercase tracking-wide mb-1">Platform Billing Basis</p>
                    <p className="text-[11px] text-muted-foreground mb-2.5">
                      Which count the base charge is billed on -- the other count still guards against it being gamed (a
                      driver-billed tenant can't run an unlimited fleet on 1 driver).
                    </p>
                    <Select
                      value={selectedTenant.platform_billing_basis}
                      onValueChange={(v) => savePlatformBillingBasis(v as PlatformBillingBasis)}
                      disabled={savingBillingBasis}
                    >
                      <SelectTrigger className="dark-select h-9 w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-black border-yellow-600">
                        <SelectItem value="drivers">Active drivers</SelectItem>
                        <SelectItem value="vehicles">Active vehicles</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <SuperadminTenantBilling
                    tenantId={selectedTenant.id}
                    stripeCustomerId={selectedTenant.stripe_customer_id}
                    paymentRestricted={selectedTenant.payment_restricted}
                    onStripeCustomerCreated={(stripeCustomerId) =>
                      setTenants((prev) => prev.map((t) => (t.id === selectedTenant.id ? { ...t, stripe_customer_id: stripeCustomerId } : t)))
                    }
                  />

                  {modulesLoading ? (
                    <p className="text-sm text-muted-foreground">Loading modules...</p>
                  ) : (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                      {orderedModules.map((m) => {
                        const Icon = moduleIcon(m.module_key);
                        const isCore = CORE_MODULE_ORDER.includes(m.module_key);
                        const enabled = isCore || (enabledByModuleKey[m.module_key] ?? false);
                        const controls = MODULE_TABS[m.module_key] ?? [];
                        return (
                          <div
                            key={m.module_key}
                            className={`flex flex-col gap-3 p-3.5 rounded-xl border transition-colors ${
                              enabled
                                ? 'border-green-900/40 bg-green-900/10 shadow-[0_0_18px_-6px_rgba(34,197,94,0.45)]'
                                : 'border-red-900/30 bg-red-900/5 shadow-[0_0_14px_-6px_rgba(239,68,68,0.3)]'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div
                                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                  enabled ? 'bg-gradient-to-br from-green-400 to-emerald-600' : 'bg-secondary'
                                }`}
                              >
                                <Icon className={`h-4 w-4 ${enabled ? 'text-black' : 'text-muted-foreground'}`} />
                              </div>
                              {isCore ? (
                                <span
                                  title="Base app -- always available, not a paid module"
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-green-900/20 border border-green-900/40 text-green-500 shrink-0"
                                >
                                  <CheckCircle2 className="h-3 w-3" />
                                  Always on
                                </span>
                              ) : (
                                <Switch
                                  checked={enabled}
                                  onCheckedChange={(checked) => toggleModule(m.module_key, checked)}
                                  disabled={savingKey === m.module_key}
                                />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">{m.module_name}</p>
                              {m.description && <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>}
                            </div>
                            <div className="mt-auto pt-2 border-t border-border/60">
                              {controls.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {controls.map((c) => {
                                    const CIcon = c.icon;
                                    return (
                                      <span
                                        key={c.label}
                                        title={c.label}
                                        className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-secondary/60 text-muted-foreground"
                                      >
                                        <CIcon className="h-3 w-3" />
                                        {c.label}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <p className="text-[10px] text-muted-foreground/70 italic">Core view -- not tied to a specific tab</p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </>
            ) : (
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4" />
                  Select a tenant to manage its modules.
                </p>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      <Dialog open={showAddTenant} onOpenChange={setShowAddTenant}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Tenant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tenant-name">Company Name</Label>
              <Input
                id="tenant-name"
                value={newTenantName}
                onChange={(e) => setNewTenantName(e.target.value)}
                placeholder="e.g. Wellington Taxis Ltd"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenant-trading-name">Trading Name (optional)</Label>
              <Input
                id="tenant-trading-name"
                value={newTenantTradingName}
                onChange={(e) => setNewTenantTradingName(e.target.value)}
                placeholder="What shows on screen if different"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tenant-login-code">Company Login Code</Label>
              <Input
                id="tenant-login-code"
                value={newTenantLoginCode}
                onChange={(e) => setNewTenantLoginCode(e.target.value)}
                placeholder="What their team types to reach this company"
              />
              <p className="text-xs text-muted-foreground">
                Every tenant needs this to actually reach their login screen -- can be added later, but nobody there can sign
                in until it's set.
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              No modules are enabled yet -- turn them on for this tenant after creating it, on the tile grid below.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddTenant(false)}>
              Cancel
            </Button>
            <Button onClick={createTenant} disabled={creatingTenant || !newTenantName.trim()} className="golden-button">
              {creatingTenant ? 'Creating...' : 'Create Tenant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Admin -- {selectedTenant?.trading_name || selectedTenant?.tenant_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Name</Label>
              <Input id="admin-name" value={newAdminName} onChange={(e) => setNewAdminName(e.target.value)} placeholder="e.g. Sam Taylor" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email (optional)</Label>
              <Input
                id="admin-email"
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="them@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password</Label>
              <Input
                id="admin-password"
                type="text"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="At least 8 characters"
              />
              <p className="text-xs text-muted-foreground">
                Shown in plain text here so you can note it down to share with them -- it's hashed before it's ever stored.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdmin(false)}>
              Cancel
            </Button>
            <Button
              onClick={createAdmin}
              disabled={creatingAdmin || !newAdminName.trim() || newAdminPassword.length < 8}
              className="golden-button"
            >
              {creatingAdmin ? 'Creating...' : 'Create Login'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperadminDashboard;
